/**
 * Helper functions to detect rental detail paths and extract rental IDs
 */

/**
 * Checks if the current path is a rental detail page (not payments or other tabs)
 */
export const isRentalDetailPath = (pathname: string): boolean => {
  const rentalDetailRegex = /^\/orders\/rentals\/([^/]+)$/;
  return rentalDetailRegex.test(pathname);
};

/**
 * Extracts the rental ID from a rental-related URL path
 */
export const extractRentalIdFromPath = (pathname: string): string | null => {
  // Match both the detail page and any sub-pages (payments, display, etc.)
  const rentalPathRegex = /^\/orders\/rentals\/([^/]+)(?:\/.*)?$/;
  const match = pathname.match(rentalPathRegex);

  // Return null for special paths like 'all', 'pending', 'new', etc.
  if (
    match &&
    [
      'all',
      'pending',
      'quotes',
      'reservations',
      'checked_out',
      'checked_in',
      'closed',
      'cancelled',
      'drafts',
      'templates',
      'new',
    ].includes(match[1])
  ) {
    return null;
  }

  return match ? match[1] : null;
};

/**
 * Checks if the current path is a rental payments page
 */
export const isRentalPaymentsPath = (pathname: string): boolean => {
  const rentalPaymentsRegex = /^\/orders\/rentals\/([^/]+)\/payments$/;
  return rentalPaymentsRegex.test(pathname);
};

/**
 * Builds a path to the payments page for a specific rental
 */
export const buildRentalPaymentsPath = (rentalId: string): string => {
  return `/orders/rentals/${rentalId}/payments`;
};

/**
 * Checks if the path is a valid rental detail page with a specific rental ID
 * (not a special path like 'all', 'pending', etc.)
 */
export const isValidRentalIdPath = (pathname: string): boolean => {
  const rentalId = extractRentalIdFromPath(pathname);
  return rentalId !== null;
};
